import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { buildTranslator, CustomFormActivityListing, DateBuilder, rng } from '../../amorg-common';
import { CustomFormTemplate } from '../../amorg-common/DTOs/CustomFormTemplate';
import { createActivity } from '../../api/activityClientApi';
import { createCustomForm, getCustomFormActivityList, getCustomFormTemplates } from '../../api/customFormClientApi';
import { Button } from '../../designSystem/button/Button';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSectionTable } from '../../designSystem/PageSectionTable';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { Icon } from '../../designSystem/icons/Icons';
import { colors } from '../../designSystem/colors/color';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { Card } from '../../designSystem/Card';
import { Select } from '../../designSystem/inputs/Select';
import { Modal } from '../../designSystem/Modal';
import { P } from '../../designSystem/typography/Body';

export function ErgonomicsPage():JSX.Element
{
    const [state, setState] = React.useState<{
        isLoading:boolean, 
        templates:CustomFormTemplate[], 
        activities:CustomFormActivityListing[]
        selectedTemplateId:string,
        showModal:boolean,
        redirectId:string
    }>(
        { isLoading:true, templates:[], activities:[], selectedTemplateId:'', showModal:false, redirectId:'' }
    );

    React.useEffect(()=>{
        const getData = async () => {
            const templates = await getCustomFormTemplates();
            const activities = await getCustomFormActivityList();
            setState({ isLoading:false, activities, templates, selectedTemplateId:'', showModal:false, redirectId:'' });
        }
        getData();
    }, []);

    async function handleClickCreate()
    {
        const activityId = rng.getRandomId();
        const formId = rng.getRandomId();
        const template = state.templates.find(t => t.templateId === state.selectedTemplateId);
        if(!template)
            return;

        const activityResult = await createActivity(
            activityId, 
            'CustomForm',
            'Ny undersøgelse',
            translator(template.description),
            DateBuilder.now().date, 
            DateBuilder.now().addMonths(3).date, 
        '');

        if(activityResult.isSuccess)
        {
            const formResult = await createCustomForm(formId, activityId, state.selectedTemplateId);
            if(formResult.isSuccess)
            {
                setState({...state, redirectId:activityId});
            }
        }
    }

    const current = state.activities?.filter(a => a.end >= new Date()) || [];
    const past = state.activities?.filter(a => a.end < new Date()) || [];

    const translator = buildTranslator('da'); 

    return (
        
        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey='ergonomics'>
            <SingleColumnLayout  
                noDivider
                contentContainer={
                    <>
                        {state.redirectId !== '' &&
                            <Navigate to={`/customforms/${state.redirectId}`} />
                        }

                        <LoadingPageOverlay loading={state.isLoading} />

                        <PageHeader
                            headerText="Aktuelle Ergonomiske undersøgelser"
                            headerStyle="primary-black"
                            headerIcon="physical_therapy"
                            buttons={
                                <Button variant="filled" text="Opret ny undersøgelse" icon="actions_plus" onClick={() => setState({...state, showModal:true})} />
                            }
                        />

                        <PageSectionTable
                            headerColumns={[
                                {text:'Status', gridDefinition:'20%'},
                                {text:'Undersøgelse', gridDefinition:'minmax(0, 1fr)'},
                                {text:'Besvarelser', gridDefinition:'minmax(0, 1fr)'},
                                {text:'', gridDefinition:'min-content'}
                            ]}
                            noContentText="Ingen aktuelle undersøgelser"
                            showAllLimit={5}>

                            {current.map(activity => {
                                

                                return (
                                    <>
                                        <StatusContent activity={activity} />

                                        <SurveyContent activity={activity} />

                                        <AnswerContent activity={activity} />

                                        <LinkButton variant="text"
                                            icon="arrows_chevron_chevronForward"
                                            href={`/customforms/${activity.id}`} />
                                    </>
                                );
                            })}
                        </PageSectionTable>

                        {past.length > 0 &&
                            <>
                                <PageHeader
                                    headerText="Tidligere Ergonomiske undersøgelser"
                                    headerStyle="primary-black"
                                />

                                <PageSectionTable
                                    headerColumns={[
                                        {text:'Status', gridDefinition:'20%'},
                                        {text:'Undersøgelse', gridDefinition:'minmax(0, 1fr)'},
                                        {text:'Besvarelser', gridDefinition:'minmax(0, 1fr)'},
                                        {text:'', gridDefinition:'min-content'}
                                    ]}
                                    noContentText="Ingen tidligere undersøgelser"
                                    showAllLimit={5}>

                                    {past.map(activity => {
                                        

                                        return (
                                            <>
                                                <StatusContent activity={activity} />

                                                <SurveyContent activity={activity} />

                                                <AnswerContent activity={activity} />

                                                <LinkButton variant="text"
                                                    icon="arrows_chevron_chevronForward"
                                                    href={`/customforms/${activity.id}`} />
                                            </>
                                        );
                                    })}
                                </PageSectionTable>
                            </>
                        }

                        <Modal title={'Opret ny undersøgelse'} 
                            showModal={state.showModal}
                            onCancel={() => setState({...state, showModal:false})}
                            content={
                                <Flex flexDirection="column" gap={24}>
                                    <Select 
                                        selectedValue={state.selectedTemplateId} 
                                        options={
                                            [
                                                { value:'', text:'Vælg skabelon' },
                                                ...state.templates.map(t => ({ value:t.templateId, text:translator(t.name) }))
                                            ]
                                        } 
                                        onChange={function (newValue: string): void
                                        {
                                            setState({ ...state, selectedTemplateId:newValue });
                                        }}
                                    />

                                    
                                    {state.selectedTemplateId==='' 
                                        ?   <P>Vælg en skabelon for at fortsætte</P> 
                                        :   <Card>
                                                {translator(state.templates.find(t => t.templateId === state.selectedTemplateId)?.description  || [])}
                                            </Card>
                                    }

                                    <ButtonRow>
                                        <Button 
                                            variant='filled' 
                                            text="Opret" 
                                            disabled={state.selectedTemplateId===''}
                                            onClick={() => handleClickCreate() }
                                        />
                                    </ButtonRow>
                                </Flex>
                            } 
                        />
                    </>
                } 
            />
        </ProtectedPageWrapper>
    );
}

const activityStatus = (activity:CustomFormActivityListing): 'planned' | 'open' | 'closed' => {
    const now = new Date();
    if(activity.start > now)
        return 'planned';
    if(activity.end < now)
        return 'closed';
    return 'open';
}

function StatusContent({activity}:{activity:CustomFormActivityListing}):JSX.Element
{
    if(activityStatus(activity) === 'planned')
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="fact_check" color={colors.icon.active} />Planlagt</Flex>;
    else {
        if(activityStatus(activity) === 'closed')
            return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />Afsluttet</Flex>;
        else
            return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="interface_glyph_hourglass" color={colors.icon.warning} />Under afvikling</Flex>;
    }
}

function SurveyContent({activity}:{activity:CustomFormActivityListing}):JSX.Element
{
    return <Flex flexDirection="row" alignItems="center" gap={4}>{activity.formName}</Flex>;
}

function AnswerContent({activity}:{activity:CustomFormActivityListing}):JSX.Element
{
    if(activity.answerCount === 0)
        return <Flex flexDirection="row" alignItems="center" gap={4}>Ingen besvarelser</Flex>;
    else if(activity.answerCount === 1)
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />1 besvarelse</Flex>;
    else
        return <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="status_ok" color={colors.icon.success} />{activity.answerCount} besvarelser</Flex>;
}